import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Users, Building2, FolderKanban, CheckSquare, 
  BookOpen, TrendingUp, BarChart3, LogOut, Menu, X, ChevronDown,
  Settings, FileEdit, Target, Layers, GanttChart, Sparkles, CalendarDays, Globe, GraduationCap
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import NotificationDropdown from '../NotificationDropdown';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/u6f2dm1t_CompliSun.png';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/clients', label: 'Clients', icon: Building2 },
  { path: '/matters', label: 'Matters', icon: FolderKanban },
  { path: '/tasks', label: 'Tasks', icon: CheckSquare },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
  { path: '/google-calendar', label: 'Google Cal', icon: Globe },
  { path: '/projects', label: 'Projects', icon: Layers },
  { path: '/timeline', label: 'Timeline', icon: GanttChart },
  { path: '/okrs', label: 'OKRs', icon: Target },
  { path: '/drafts', label: 'AI Drafts', icon: FileEdit },
  { path: '/team', label: 'Team', icon: Users },
  { path: '/capacity', label: 'Capacity', icon: BarChart3 },
  { path: '/leads', label: 'Leads', icon: TrendingUp },
  { path: '/bd-agent', label: 'BD Agent', icon: Sparkles },
  { path: '/knowledge', label: 'Knowledge', icon: BookOpen },
  { path: '/courses', label: 'Courses', icon: GraduationCap },
];

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F5F8FF] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <img src={LOGO_URL} alt="CompliSun" className="w-10 h-10 rounded-full bg-white" />
          <div>
            <h1 className="font-bold text-white text-lg font-['Amaranth']">Ops Hub</h1>
            <p className="text-xs text-white/60">CompliCrew Portal</p>
          </div>
          <button 
            className="lg:hidden ml-auto p-1 text-white/70 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-3 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `sidebar-link mb-1 ${isActive ? 'active' : ''}`
              }
              data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <Avatar className="w-9 h-9 bg-[#40CFDE]">
              <AvatarFallback className="bg-[#40CFDE] text-[#1A2B5E] font-medium text-sm">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/60 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top header */}
        <header className="bg-white border-b border-navy/10 px-4 py-3 flex items-center gap-4 sticky top-0 z-30">
          <button 
            className="lg:hidden p-2 -ml-2 text-navy/70 hover:text-navy"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <NotificationDropdown />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-navy/5 transition-colors"
                data-testid="user-menu-btn"
              >
                <Avatar className="w-8 h-8 bg-[#40CFDE]">
                  <AvatarFallback className="bg-[#40CFDE] text-[#1A2B5E] font-medium text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-navy/60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="font-medium text-sm">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
